import { useEffect, useMemo, useState } from 'react';
import { BarChart3, Hash, Layers, LineChart, PieChart, Plus, Sparkles, Table2 } from 'lucide-react';

import { ErrorState } from '@/components/ui/ErrorState';
import { Spinner } from '@/components/ui/Spinner';
import { ReportWidget } from '@/features/reporting/components/ReportWidget';
import { useReportDatasets } from '@/features/reporting/hooks';
import {
  AGGREGATE_LABELS,
  isNumericType,
  type Aggregate,
  type ChartType,
  type ReportDataset,
  type WidgetConfig,
} from '@/features/reporting/types';
import type { ApiError } from '@/lib/errors';

const STORAGE_KEY = 'reporting.widgets.v1';

const inputClass =
  'h-10 w-full rounded-lg border border-ink-200 bg-white px-3 text-sm text-ink-900 shadow-soft transition focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100 dark:border-white/10 dark:bg-ink-900 dark:text-ink-50';

const CHART_TYPES: Array<{ type: ChartType; label: string; icon: typeof BarChart3 }> = [
  { type: 'bar', label: 'Bar', icon: BarChart3 },
  { type: 'line', label: 'Line', icon: LineChart },
  { type: 'pie', label: 'Pie', icon: PieChart },
  { type: 'kpi', label: 'KPI', icon: Hash },
  { type: 'table', label: 'Table', icon: Table2 },
];

function uid(): string {
  return `w-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;
}

function buildTitle(agg: Aggregate, measure: string | null, dimension: string | null): string {
  const what = agg === 'count' ? 'Count' : `${AGGREGATE_LABELS[agg]} of ${measure ?? ''}`.trim();
  return dimension ? `${what} by ${dimension}` : `${what} (total)`;
}

function compareTitle(agg: Aggregate, measure: string | null, count: number): string {
  const what = agg === 'count' ? 'Record count' : `${AGGREGATE_LABELS[agg]} of ${measure ?? ''}`.trim();
  return `${what} across ${count} tables`;
}

/** Chart types that make sense when comparing one value per table. */
const COMPARE_CHART_TYPES = new Set<ChartType>(['bar', 'pie', 'table']);

export function ReportingPage(): JSX.Element {
  const datasetsQ = useReportDatasets();
  const datasets = useMemo(() => datasetsQ.data ?? [], [datasetsQ.data]);

  const [mode, setMode] = useState<'single' | 'compare'>('single');
  const [datasetId, setDatasetId] = useState('');
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [dimension, setDimension] = useState('');
  const [measure, setMeasure] = useState('');
  const [aggregate, setAggregate] = useState<Aggregate>('count');
  const [limit, setLimit] = useState(10);

  const [widgets, setWidgets] = useState<WidgetConfig[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? (JSON.parse(raw) as WidgetConfig[]) : [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
  }, [widgets]);

  const selected: ReportDataset | undefined = datasets.find((d) => d.id === datasetId);
  const columns = selected?.columns ?? [];
  const numericColumns = columns.filter((c) => isNumericType(c.data_type));

  // Compare mode: numeric columns present in *every* selected table, so the
  // same measure can be aggregated across all of them safely.
  const compareNumeric = useMemo(() => {
    const picked = datasets.filter((d) => compareIds.includes(d.id));
    if (picked.length === 0) return [];
    const sets = picked.map(
      (d) => new Set(d.columns.filter((c) => isNumericType(c.data_type)).map((c) => c.name)),
    );
    const [first, ...rest] = sets;
    return [...(first ?? [])].filter((n) => rest.every((s) => s.has(n)));
  }, [datasets, compareIds]);

  function toggleCompareId(id: string): void {
    setCompareIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]));
  }

  // Default the builder to the first dataset once they load.
  useEffect(() => {
    if (datasetId === '' && datasets.length > 0) {
      const first = datasets[0]!;
      setDatasetId(first.id);
      setDimension(first.columns[0]?.name ?? '');
      const num = first.columns.find((c) => isNumericType(c.data_type));
      setMeasure(num?.name ?? '');
    }
  }, [datasets, datasetId]);

  function onDatasetChange(id: string): void {
    setDatasetId(id);
    const ds = datasets.find((d) => d.id === id);
    setDimension(ds?.columns[0]?.name ?? '');
    const num = ds?.columns.find((c) => isNumericType(c.data_type));
    setMeasure(num?.name ?? '');
  }

  function addWidget(): void {
    const usesMeasure = aggregate !== 'count';
    const m = usesMeasure ? measure || null : null;
    if (usesMeasure && !m) {
      return;
    }

    if (mode === 'compare') {
      if (compareIds.length < 2) return;
      // Measure must exist in every selected table (count needs no measure).
      if (m !== null && !compareNumeric.includes(m)) return;
      const names = compareIds.map((id) => datasets.find((d) => d.id === id)?.name ?? id);
      const ct = COMPARE_CHART_TYPES.has(chartType) ? chartType : 'bar';
      setWidgets((w) => [
        {
          id: uid(),
          title: compareTitle(aggregate, m, compareIds.length),
          mode: 'compare',
          datasetId: '',
          datasetName: '',
          datasetIds: compareIds,
          datasetNames: names,
          chartType: ct,
          dimension: null,
          measure: m,
          aggregate,
          limit: 1,
        },
        ...w,
      ]);
      return;
    }

    if (datasetId === '' || !selected) {
      return;
    }
    const dim = chartType === 'kpi' ? null : dimension || null;
    setWidgets((w) => [
      {
        id: uid(),
        title: buildTitle(aggregate, m, dim),
        mode: 'single',
        datasetId,
        datasetName: selected.name,
        chartType,
        dimension: dim,
        measure: m,
        aggregate,
        limit,
      },
      ...w,
    ]);
  }

  function removeWidget(id: string): void {
    setWidgets((w) => w.filter((x) => x.id !== id));
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow eyebrow-dot mb-2.5">
          <span>MODULE</span>
        </div>
        <h1 className="h-display">Reporting</h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-ink-500 dark:text-ink-400">
          Build live charts from any connected data source — chart a single dataset grouped by any column, or
          switch to <strong>Compare tables</strong> to measure the same metric across several datasets at once.
          Like a lightweight Power BI, running on your governed data.
        </p>
      </div>

      {/* Builder */}
      <section className="rounded-xl border border-ink-200/70 bg-white p-5 shadow-soft dark:border-white/10 dark:bg-ink-900">
        <h2 className="mb-4 font-display text-[15px] font-semibold tracking-tight text-ink-900 dark:text-ink-50">
          Build a chart
        </h2>

        {datasetsQ.isPending ? (
          <Spinner label="Loading datasets…" />
        ) : datasetsQ.isError ? (
          <ErrorState error={datasetsQ.error as ApiError} />
        ) : datasets.length === 0 ? (
          <p className="text-sm text-ink-500 dark:text-ink-400">
            No datasets yet — connect a database or upload a file in <strong>Data Sources</strong> first.
          </p>
        ) : (
          <div className="space-y-4">
            {/* Mode toggle: one dataset vs compare across tables */}
            <div className="inline-flex rounded-lg border border-ink-200 p-0.5 dark:border-white/10">
              {([
                { id: 'single', label: 'Single dataset', icon: BarChart3 },
                { id: 'compare', label: 'Compare tables', icon: Layers },
              ] as const).map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    setMode(id);
                    if (id === 'compare' && !COMPARE_CHART_TYPES.has(chartType)) setChartType('bar');
                  }}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition ${
                    mode === id
                      ? 'bg-brand-600 text-white shadow-sm'
                      : 'text-ink-600 hover:bg-ink-50 dark:text-ink-300 dark:hover:bg-white/5'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>

            {/* Chart type toggle */}
            <div className="flex flex-wrap gap-2">
              {CHART_TYPES.map(({ type, label, icon: Icon }) => {
                const disabled = mode === 'compare' && !COMPARE_CHART_TYPES.has(type);
                return (
                  <button
                    key={type}
                    type="button"
                    disabled={disabled}
                    onClick={() => setChartType(type)}
                    title={disabled ? 'Not available when comparing tables' : undefined}
                    className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                      chartType === type
                        ? 'border-brand-300 bg-brand-50 text-brand-700 dark:border-brand-500/30 dark:bg-brand-500/15 dark:text-brand-300'
                        : 'border-ink-200 text-ink-600 hover:bg-ink-50 dark:border-white/10 dark:text-ink-300 dark:hover:bg-white/5'
                    } ${disabled ? 'cursor-not-allowed opacity-40 hover:bg-transparent' : ''}`}
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </button>
                );
              })}
            </div>

            {mode === 'single' ? (
              <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                <label className="space-y-1 md:col-span-4">
                  <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Data source</span>
                  <select className={inputClass} value={datasetId} onChange={(e) => onDatasetChange(e.target.value)}>
                    {datasets.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                        {d.connector ? ` — ${d.connector}` : ''}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1 md:col-span-3">
                  <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Aggregate</span>
                  <select
                    className={inputClass}
                    value={aggregate}
                    onChange={(e) => setAggregate(e.target.value as Aggregate)}
                  >
                    {(Object.keys(AGGREGATE_LABELS) as Aggregate[]).map((a) => (
                      <option key={a} value={a}>
                        {AGGREGATE_LABELS[a]}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1 md:col-span-3">
                  <span className="text-xs font-medium text-ink-600 dark:text-ink-300">
                    Measure {aggregate === 'count' ? '(optional)' : ''}
                  </span>
                  <select
                    className={inputClass}
                    value={measure}
                    onChange={(e) => setMeasure(e.target.value)}
                    disabled={aggregate === 'count'}
                  >
                    <option value="">— rows —</option>
                    {(aggregate === 'count' ? columns : numericColumns).map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="space-y-1 md:col-span-2">
                  <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Top N</span>
                  <input
                    type="number"
                    min={1}
                    max={200}
                    className={inputClass}
                    value={limit}
                    onChange={(e) => setLimit(Math.max(1, Math.min(200, Number(e.target.value) || 10)))}
                  />
                </label>

                <label className="space-y-1 md:col-span-10">
                  <span className="text-xs font-medium text-ink-600 dark:text-ink-300">
                    Group by {chartType === 'kpi' ? '(ignored for KPI)' : ''}
                  </span>
                  <select
                    className={inputClass}
                    value={dimension}
                    onChange={(e) => setDimension(e.target.value)}
                    disabled={chartType === 'kpi'}
                  >
                    <option value="">— none (total) —</option>
                    {columns.map((c) => (
                      <option key={c.name} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="flex items-end md:col-span-2">
                  <button
                    type="button"
                    onClick={addWidget}
                    className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white transition hover:bg-brand-700"
                  >
                    <Plus className="h-4 w-4" />
                    Add
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Multi-table picker */}
                <div className="space-y-1.5">
                  <span className="text-xs font-medium text-ink-600 dark:text-ink-300">
                    Tables to compare <span className="text-ink-400 dark:text-ink-500">(pick 2 or more)</span>
                  </span>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                    {datasets.map((d) => {
                      const checked = compareIds.includes(d.id);
                      return (
                        <label
                          key={d.id}
                          className={`flex cursor-pointer items-center gap-2.5 rounded-lg border px-3 py-2 text-sm transition ${
                            checked
                              ? 'border-brand-300 bg-brand-50 dark:border-brand-500/30 dark:bg-brand-500/10'
                              : 'border-ink-200 hover:bg-ink-50 dark:border-white/10 dark:hover:bg-white/5'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleCompareId(d.id)}
                            className="h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-400"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium text-ink-800 dark:text-ink-100">{d.name}</span>
                            <span className="block truncate text-[11px] text-ink-400 dark:text-ink-500">
                              {d.connector ?? 'dataset'}
                              {d.row_count != null ? ` · ${d.row_count.toLocaleString()} rows` : ''}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
                  <label className="space-y-1 md:col-span-4">
                    <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Aggregate</span>
                    <select
                      className={inputClass}
                      value={aggregate}
                      onChange={(e) => setAggregate(e.target.value as Aggregate)}
                    >
                      {(Object.keys(AGGREGATE_LABELS) as Aggregate[]).map((a) => (
                        <option key={a} value={a}>
                          {AGGREGATE_LABELS[a]}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-1 md:col-span-6">
                    <span className="text-xs font-medium text-ink-600 dark:text-ink-300">
                      Measure {aggregate === 'count' ? '(rows)' : '(shared numeric column)'}
                    </span>
                    <select
                      className={inputClass}
                      value={measure}
                      onChange={(e) => setMeasure(e.target.value)}
                      disabled={aggregate === 'count'}
                    >
                      <option value="">— row count —</option>
                      {compareNumeric.map((name) => (
                        <option key={name} value={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="flex items-end md:col-span-2">
                    <button
                      type="button"
                      onClick={addWidget}
                      disabled={compareIds.length < 2 || (aggregate !== 'count' && !measure)}
                      className="inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-lg bg-brand-600 px-3 text-sm font-medium text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      <Plus className="h-4 w-4" />
                      Add
                    </button>
                  </div>
                </div>

                {aggregate !== 'count' && compareIds.length >= 2 && compareNumeric.length === 0 && (
                  <p className="text-xs text-accent-amber">
                    The selected tables share no common numeric column — switch to <strong>Count</strong> to compare
                    row volumes, or pick tables with a matching measure.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Canvas */}
      {widgets.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ink-300 bg-white/60 p-12 text-center dark:border-white/15 dark:bg-white/5">
          <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-xl bg-brand-50 text-brand-600 dark:bg-brand-900/30">
            <Sparkles className="h-5 w-5" />
          </div>
          <p className="mt-3 text-sm text-ink-600 dark:text-ink-300">Your report is empty.</p>
          <p className="mt-1 text-xs text-ink-400 dark:text-ink-500">
            Configure a chart above and click <strong>Add</strong> to start building.
          </p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {widgets.map((w) => (
            <ReportWidget key={w.id} widget={w} onRemove={removeWidget} />
          ))}
        </div>
      )}
    </div>
  );
}
